/* eslint-disable max-lines */
import { Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { apiCall } from "../../config/api";
import { LoadingSpinner } from "../../shared/components/loading/LoadingSpinner";
import { Button } from "../../shared/components/ui/button";
import { cn } from "../../shared/components/ui/cn";
import { useToast } from "../../shared/hooks/use-toast";
import { useCascadeFadeIn } from "../../shared/hooks/useCascadeFadeIn";
import { CollectionDetailsView } from "./components/CollectionDetailsView";
import type { Collection, CollectionDetails } from "./types/collections";

/**
 * Phantom collection key used when creating a new collection inline
 */
const PHANTOM_COLLECTION_KEY = "__creating__";

interface CollectionListProps {
  readonly collections: Collection[];
  readonly selectedKey: string | null;
  readonly isCreatingNew: boolean;
  readonly onSelect: (key: string) => void;
  readonly onCreateNew: () => void;
  readonly onDelete: (collection: Collection) => void;
  readonly collectionItemCounts: Map<string, number>;
}

interface CollectionListItemProps {
  readonly collection: Collection;
  readonly index: number;
  readonly itemCount: number;
  readonly isSelected: boolean;
  readonly isPhantom?: boolean;
  readonly onSelect: () => void;
  readonly onDelete: () => void;
}

/**
 * CollectionListItem - Individual collection item with animation
 */
function CollectionListItem({
  collection,
  index,
  itemCount,
  isSelected,
  isPhantom = false,
  onSelect,
  onDelete,
}: CollectionListItemProps): ReactElement {
  const { ref, isVisible } = useCascadeFadeIn({ index, threshold: 0.1, staggerDelay: 50 });

  return (
    <div
      ref={ref}
      className={cn(
        "rounded-lg transition-all duration-500 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
        isSelected
          ? "border border-lumi-accent text-white"
          : " border border-transparent text-white hover:border-neutral-700",
        isPhantom && "opacity-70",
      )}
    >
      <div className="flex items-center justify-between px-4 py-3">
        <button onClick={onSelect} className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="font-medium">{collection.name}</span>
            {!isPhantom && (
              <span title={collection.visible ? "Visible" : "Hidden"}>
                {collection.visible ? (
                  <Eye size={14} className="text-green-500" />
                ) : (
                  <EyeOff size={14} className="text-neutral-500" />
                )}
              </span>
            )}
          </div>
          {!isPhantom && (
            <div className="text-xs opacity-70 mt-1">
              {collection.key} • Sort: {collection.sort} • {itemCount} {itemCount === 1 ? "item" : "items"}
            </div>
          )}
          {isPhantom && <div className="text-xs opacity-50 mt-1">Fill in the details to create this collection</div>}
        </button>
        {!isPhantom && (
          <div className="flex gap-1 ml-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1.5 rounded hover:text-red-500 transition"
              title="Delete collection"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * CollectionList Component - Sidebar with collection list
 */

function CollectionList({
  collections,
  selectedKey,
  isCreatingNew,
  onSelect,
  onCreateNew,
  onDelete,
  collectionItemCounts,
}: CollectionListProps): ReactElement {
  // Create phantom collection for inline creation
  const phantomCollection: Collection = {
    key: PHANTOM_COLLECTION_KEY,
    name: "New Collection",
    sort: 0,
    cardType: "VERTICAL",
    visible: true,
    template: "RAIL",
  };

  const displayCollections = isCreatingNew ? [phantomCollection, ...collections] : collections;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-white">Collections</h3>
        <Button onClick={onCreateNew} size="sm" variant="ghost-sm" disabled={isCreatingNew}>
          <Plus size={16} className="mr-1" />
          New
        </Button>
      </div>
      <div className="space-y-2">
        {displayCollections.map((collection, index) => (
          <CollectionListItem
            key={collection.key}
            collection={collection}
            index={index}
            itemCount={collectionItemCounts.get(collection.key) ?? 0}
            isSelected={selectedKey === collection.key}
            isPhantom={collection.key === PHANTOM_COLLECTION_KEY}
            onSelect={() => {
              if (document.startViewTransition) {
                document.startViewTransition(() => {
                  onSelect(collection.key);
                });
              } else {
                onSelect(collection.key);
              }
            }}
            onDelete={() => onDelete(collection)}
          />
        ))}
        {collections.length === 0 && !isCreatingNew && (
          <div className="text-center py-8 text-neutral-500">
            <p className="mb-4">No collections yet</p>
            <Button onClick={onCreateNew} size="sm">
              <Plus size={16} className="mr-2" />
              Create First Collection
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * CollectionsPage - Complete collections management interface
 */
// eslint-disable-next-line max-lines-per-function
export function CollectionsPage(): ReactElement {
  const { toast } = useToast();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [collectionDetails, setCollectionDetails] = useState<CollectionDetails | null>(null);
  const [collectionItemCounts, setCollectionItemCounts] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [deletingCollection, setDeletingCollection] = useState<Collection | null>(null);

  const loadCollections = async () => {
    try {
      const response = await apiCall("/v1/admin/collections");
      if (!response.ok) {
        throw new Error(`Failed to load collections: ${response.status}`);
      }
      const data = (await response.json()) as Collection[];
      setCollections(data);

      // Load item counts for each collection
      const counts = new Map<string, number>();
      await Promise.all(
        data.map(async (collection) => {
          try {
            const detailsResponse = await apiCall(`/v1/collections/${collection.key}`);
            if (detailsResponse.ok) {
              const details = (await detailsResponse.json()) as CollectionDetails;
              // Count both items and providers (PROVIDER template uses providers array)
              const itemCount = details.items.length + (details.providers?.length ?? 0);
              counts.set(collection.key, itemCount);
            }
          } catch (err) {
            console.error(`Failed to load count for ${collection.key}:`, err);
          }
        }),
      );
      setCollectionItemCounts(counts);

      setLoading(false);
    } catch (err) {
      console.error("❌ Failed to load collections:", err);
      setError("Failed to load collections");
      setLoading(false);
    }
  };

  const loadCollectionDetails = async (key: string) => {
    setDetailsLoading(true);
    try {
      const response = await apiCall(`/v1/collections/${key}`);
      if (!response.ok) {
        throw new Error(`Failed to load collection: ${response.status}`);
      }
      const data = (await response.json()) as CollectionDetails;

      // For PROVIDER template collections, merge providers into items for consistent handling
      if (data.providers && data.providers.length > 0) {
        data.items = data.providers;
      }

      setCollectionDetails(data);

      // Update item count in the map
      setCollectionItemCounts((prev) => {
        const updated = new Map(prev);
        updated.set(key, data.items.length);
        return updated;
      });
    } catch (err) {
      console.error(`❌ Failed to load collection ${key}:`, err);
      setError("Failed to load collection details");
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    void loadCollections();
  }, []);

  useEffect(() => {
    if (!selectedCollection) {
      setCollectionDetails(null);
      return;
    }

    void loadCollectionDetails(selectedCollection);
  }, [selectedCollection]);

  const handleRefreshDetails = () => {
    if (selectedCollection && selectedCollection !== PHANTOM_COLLECTION_KEY) {
      void loadCollectionDetails(selectedCollection);
    }
  };

  const handleCreateNew = () => {
    setIsCreatingNew(true);
    setSelectedCollection(PHANTOM_COLLECTION_KEY);
  };

  const handleCreateSuccess = (createdKey: string) => {
    setIsCreatingNew(false);
    void loadCollections();
    // Select the newly created collection
    setSelectedCollection(createdKey);
  };

  const handleCreateCancel = () => {
    setIsCreatingNew(false);
    setSelectedCollection(null);
  };

  const handleDeleteCollection = (collection: Collection) => {
    setDeletingCollection(collection);
  };

  const confirmDelete = async () => {
    if (!deletingCollection) return;

    try {
      const response = await apiCall(`/v1/admin/collections/${deletingCollection.key}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`Failed to delete collection: ${response.status}`);
      }

      // If we deleted the currently selected collection, clear selection
      if (selectedCollection === deletingCollection.key) {
        setSelectedCollection(null);
        setCollectionDetails(null);
      }

      toast({
        description: `Collection "${deletingCollection.name}" deleted successfully`,
      });

      setDeletingCollection(null);
      void loadCollections();
    } catch (err) {
      console.error("Failed to delete collection:", err);
      toast({
        description: "Failed to delete collection. Please try again.",
        variant: "destructive",
      });
    }
  };

  const cancelDelete = () => {
    setDeletingCollection(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && collections.length === 0) {
    return (
      <div className="bg-red-900/20 border border-red-800 rounded-lg p-6">
        <p className="text-red-400">{error}</p>
        <Button onClick={() => void loadCollections()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 py-8">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Collections Management</h2>
        <p className="text-sm text-neutral-400 pb-6">
          Create and organize collections of titles. Drag items to reorder within each collection.
        </p>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Sidebar: Collection List */}
        <div className="lg:border-r lg:border-neutral-800 lg:pr-8">
          <CollectionList
            collections={collections}
            selectedKey={selectedCollection}
            isCreatingNew={isCreatingNew}
            onSelect={setSelectedCollection}
            onCreateNew={handleCreateNew}
            onDelete={handleDeleteCollection}
            collectionItemCounts={collectionItemCounts}
          />
        </div>

        {/* Main Content: Collection Details */}
        <div className="lg:col-span-2 space-y-6">
          <CollectionDetailsView
            details={collectionDetails}
            isLoading={detailsLoading}
            collectionKey={selectedCollection}
            isCreatingNew={isCreatingNew}
            onRefresh={handleRefreshDetails}
            onCreateSuccess={handleCreateSuccess}
            onCreateCancel={handleCreateCancel}
          />
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deletingCollection && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 rounded-lg border border-neutral-800 w-full max-w-md p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Delete Collection?</h2>
            <p className="text-neutral-300 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-white">{deletingCollection.name}</span>? This will remove all items
              from the collection. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={cancelDelete} className="flex-1">
                Cancel
              </Button>
              <Button onClick={() => void confirmDelete()} className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
